import { c as _c } from "react/compiler-runtime";
import type { AuthProviderContext, AuthProviderProps } from '@newfront-insurance/next-auth';
import type { Provider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import type { ConfigType } from '@newfront-insurance/shared-public-config';
import { useSuspenseQuery } from '@tanstack/react-query';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import type { JSX } from 'react';
interface Props {
  children: React.ReactNode;
  config: ConfigType;
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
}

/**
 * Initialises LaunchDarkly and automatically updates the current LaunchDarkly user whenever the auth state changes. We don't
 * set the user here and we just assume they're anonymous. We'll use the provider below to keep the LaunchDarkly user and the Newfront
 * user in sync.
 */
export function FeatureFlagProvider(t0) {
  const $ = _c(7);
  const {
    children,
    authProvider,
    config
  } = t0;
  const {
    LAUNCH_DARKLY
  } = config;
  const {
    userDetails: user
  } = useProvider(authProvider);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["LaunchDarkly"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== LAUNCH_DARKLY || $[2] !== user) {
    t2 = {
      queryKey: t1,
      queryFn: () => asyncWithLDProvider({
        clientSideID: LAUNCH_DARKLY.PROJECTS.ADMIN_DASH,
        options: {
          bootstrap: "localStorage"
        },
        reactOptions: {
          useCamelCaseFlagKeys: false
        },
        context: user ? {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          key: user.uuid,
          anonymous: false
        } : {
          anonymous: true
        }
      }),
      refetchInterval: 0,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false
    };
    $[1] = LAUNCH_DARKLY;
    $[2] = user;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: LDAsyncProvider
  } = useSuspenseQuery(t2);
  if (!LDAsyncProvider) {
    return null;
  }
  let t3;
  if ($[4] !== LDAsyncProvider || $[5] !== children) {
    t3 = <LDAsyncProvider>{children} </LDAsyncProvider>;
    $[4] = LDAsyncProvider;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}